/**
 * Checks if the provided source string is a valid KnownPrefillSource.
 * List should match the same list as in type KnownPrefillSource
 */
import ExperimentService from '../services/experiment-service'
import {
  KnownPrefillSource,
  KnownSource,
  knownPrefillSources
} from '../types/Source'
import { zaPrefillAttributeMissing } from './monitoring-utils/monitoring-utils'
import { EXPERIMENTNAME } from './experiment-utils/experiments'
import { ApplicationFormAnswers } from '../components/ApplicationForm/ApplicationFormContext'
import { isNullOrEmpty } from './utils'

export const isPriceComparison = (
  source: string
): source is KnownPrefillSource => {
  return (
    (source && knownPrefillSources.includes(source as KnownPrefillSource)) ||
    false
  )
}

export const dualBrandedSourcesNotPrefilled = [
  'credittechnologies',
  'prograd',
  '1plus1loans',
  'cardealerreviews',
  'quotezone',
  'compareni',
  'gumtree',
  'yesauto',
  'quotesearch'
]

export const affiliateSources = [
  'clearscore',
  'clearscoreew',
  'clearscorerefinance',
  'freedomfinance',
  'freedomfinance-t1',
  'aro',
  'totallymoney',
  'moneysupermarket',
  'msm',
  'choosewisely',
  'motiv',
  'motivrefinance',
  'creditengine',
  'credittechnologies',
  'prograd',
  '1plus1loans',
  'cardealerreviews',
  'quotezone',
  'compareni',
  'realrates',
  'oceanfinance',
  'loantube',
  'wowcher',
  'carmoola',
  'gumtree',
  'creditkarma',
  'yesauto',
  'quotesearch',
  'popup',
  'trustpilot',
  'motoreasy',
  'whatcar',
  'veloceslondon',
  'nouveaufinance',
  'aspire',
  'gocompare',
  'ctm',
  'digitonomy',
  'wollit',
  'allpurposeloans',
  'close',
  'experian',
  'ctm',
  'believemg'
]

export const digitalSources = ['zuto', 'tiktok', 'youtube', 'twitter', 'yahoo']

export const affiliateSourcesNotEligibleForExperimentation = [
  'realrates',
  'oceanfinance',
  'loantube'
]

export const eligibleForFormlessSources = [
  'clearscore',
  'motiv',
  'freedomfinance-t1',
  'motivrefinance',
  'experian',
  'close',
  'carmoola'
]

export const partnersWithoutFormlessFeatureControl = [
  'clearscore',
  'motiv',
  'motivrefinance',
  'close'
]

export const sourcesThatDoesntSendUsDrivingLicenceSoWeDefaultIt = [
  'clearscore',
  'freedomfinance-t1',
  'experian',
  'close',
  'carmoola'
]

const eligibleForFormlessMediumsBySource: Record<string, string[]> = {
  clearscore: ['clearscore5', 'clearscore40', 'clearscore40excl']
}

const eligibleForFormlessLendersBySource: Record<string, string[]> = {
  'freedomfinance-t1': ['zuto', 'startline', 'moneybarn', 'autolend'],
  motiv: ['zuto', 'startline', 'moneybarn', 'oodle'],
  motivrefinance: ['zuto', 'zopa'],
  experian: ['autolend', 'oodle']
}

type isEligibleForFormlessJourneyProps = {
  source: KnownSource
  medium?: string
  lenderName?: string
  prefillData?: ApplicationFormAnswers
}

interface CheckEligibilityForFormlessJourneyProps
  extends isEligibleForFormlessJourneyProps {
  deviceId: string
}

export const isEligibleForFormlessJourney = ({
  source,
  medium,
  lenderName,
  prefillData
}: isEligibleForFormlessJourneyProps): boolean => {
  const lowerSource = source.toLocaleLowerCase()
  const lowerMedium = medium?.toLocaleLowerCase() ?? ''
  const lowerLenderName = lenderName?.toLocaleLowerCase() ?? ''
  const contactNumber = prefillData?.personalDetails?.contactNumber
  // We have an eligibility requirement around having a contact number to be able to be in the formless journey, as it's a key piece of data we need to be able to pass to lenders in order for them to make a decision on the application. If we don't have this, then the user can't progress through the formless journey and so we won't show it to them.
  const hasContactNumber = Boolean(contactNumber)

  if (!hasContactNumber) {
    zaPrefillAttributeMissing({
      source,
      attribute: 'contactNumber',
      origin: 'FormlessJourney'
    })
    return false
  }

  // Send any generic (lender zuto) app without driving licence to the review page, except if its clearscore, freedomfinance, experian
  const isASourceThatDoesntSendUsDrivingLicence =
    sourcesThatDoesntSendUsDrivingLicenceSoWeDefaultIt.includes(source)
  const isMissingDrivingLicence = isNullOrEmpty(
    prefillData?.drivingLicence,
    'drivingLicence',
    source
  )
  const shouldFlipToReviewPage =
    isMissingDrivingLicence &&
    lenderName === 'zuto' &&
    !isASourceThatDoesntSendUsDrivingLicence

  if (shouldFlipToReviewPage) {
    zaPrefillAttributeMissing({
      source,
      attribute: 'drivingLicence',
      origin: 'FormlessJourney'
    })
    return false
  }

  if (!eligibleForFormlessSources.includes(lowerSource as KnownPrefillSource)) {
    return false
  }

  switch (lowerSource) {
    case 'clearscore':
      return eligibleForFormlessMediumsBySource.clearscore.includes(lowerMedium)
    case 'motiv':
    case 'motivrefinance':
    case 'freedomfinance-t1':
    case 'experian':
      return (
        eligibleForFormlessLendersBySource[lowerSource].includes(
          lowerLenderName
        ) ?? false
      )
    case 'close':
    case 'carmoola':
      return true
    default:
      return false
  }
}

// Experiments
export const experimentSuffixBySource: Partial<Record<KnownSource, string>> = {
  clearscore: 'cs',
  motiv: 'motiv',
  motivrefinance: 'motivrf',
  'freedomfinance-t1': 'fft1',
  experian: 'exp',
  close: 'cb',
  carmoola: 'cml'
}

export const findExperimentBySuffix = (
  source: KnownSource,
  experiment: string
): string | null => {
  const suffix = experimentSuffixBySource[source]
  if (!suffix) return null
  return `${experiment}-${suffix}`
}

export const getFormlessJourneyExperimentKey = async ({
  source,
  medium,
  lenderName,
  deviceId,
  prefillData
}: CheckEligibilityForFormlessJourneyProps): Promise<string | false> => {
  const isEligible = isEligibleForFormlessJourney({
    source,
    medium,
    lenderName,
    prefillData
  })
  if (!isEligible) return false

  const formlessExperimentKey = findExperimentBySuffix(
    source,
    EXPERIMENTNAME.formlessJourneyV1
  )
  if (!formlessExperimentKey) return false

  if (partnersWithoutFormlessFeatureControl.includes(source))
    return formlessExperimentKey

  const isInFormlessJourneyExperiment =
    await ExperimentService().getIsExperiment(deviceId, formlessExperimentKey)
  return isInFormlessJourneyExperiment ? formlessExperimentKey : false
}
